import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Clock3, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedReveal } from "../components/Reveal";
import { SITE, SERVICES, waLink } from "../lib/site";

const CHANNELS = [
  { icon: Phone, t: "Call Us", v: SITE.phoneDisplay, href: `tel:+${SITE.phoneRaw}`, id: "phone" },
  { icon: Mail, t: "Email", v: SITE.email, href: `mailto:${SITE.email}`, id: "email" },
  { icon: MapPin, t: "Office", v: SITE.address, href: "https://maps.google.com/?q=Delhi,India", id: "office" },
];

const ContactForm = () => {
  const location = useLocation();
  const est = location.state;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: est?.service || SERVICES[0].title,
    message: est ? `I would like a project estimate.\nBudget: ${est.budget}\nTimeline: ${est.timeline}\n\n` : "",
  });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Name, email and message are required.");
      return;
    }
    setSending(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ "form-name": "contact", ...form }).toString(),
      });
    } catch (_) {
      /* WhatsApp redirect still proceeds */
    }
    const msg = `Hello RICHIE RICH Team,\n\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone || "—"}\nService: ${form.service}\n\nMessage:\n${form.message}`;
    window.open(waLink(msg), "_blank");
    toast.success("Enquiry saved — opening WhatsApp!");
    setForm({ name: "", email: "", phone: "", service: SERVICES[0].title, message: "" });
    setSending(false);
  };

  const inputCls =
    "w-full bg-white border border-slate-300 rounded-xl px-4 py-3.5 text-sm text-ink placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-royal/50 focus:border-royal transition";

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 p-7 sm:p-9 shadow-sm" data-testid="contact-form">
      <h2 className="font-display text-2xl font-extrabold text-ink">Send Us a Message</h2>
      <p className="mt-1.5 text-sm text-slate-500">Submit the form and your message will open directly in our WhatsApp chat.</p>

      <div className="mt-7 grid sm:grid-cols-2 gap-5">
        <input className={inputCls} placeholder="Enter Your Name" value={form.name} onChange={set("name")} required data-testid="contact-input-name" />
        <input className={inputCls} type="email" placeholder="Enter Your Email" value={form.email} onChange={set("email")} required data-testid="contact-input-email" />
      </div>
      <div className="mt-5 grid sm:grid-cols-2 gap-5">
        <input className={inputCls} type="tel" placeholder="Enter Your Phone Number" value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" />
        <select className={inputCls} value={form.service} onChange={set("service")} data-testid="contact-select-service">
          {SERVICES.map((s) => (
            <option key={s.id} value={s.title}>{s.title}</option>
          ))}
          <option value="Other">Other / Not Sure</option>
        </select>
      </div>
      <textarea
        className={`${inputCls} mt-5 min-h-36 resize-y`}
        placeholder="Enter Your Message"
        value={form.message}
        onChange={set("message")}
        required
        data-testid="contact-input-message"
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-submit-button"
        className="mt-7 w-full inline-flex items-center justify-center gap-2.5 bg-navy hover:bg-royal disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-all duration-300 hover:-translate-y-0.5 shadow-lg"
      >
        {sending ? "Sending..." : (
          <>
            <Send size={17} /> Submit Enquiry
          </>
        )}
      </button>
    </form>
  );
};

const Contact = () => (
  <>
    <section className="relative bg-navy text-white overflow-hidden grain" data-testid="contact-header">
      <div className="absolute inset-0 hero-grid" />
      <div className="absolute -top-32 right-1/4 w-96 h-96 rounded-full bg-royal/25 blur-[120px]" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
        <p className="text-xs uppercase tracking-[0.3em] font-bold text-gold">Contact Us</p>
        <h1 className="mt-4 font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="contact-heading">
          <MaskedReveal delay={0.1}>Let's Talk About</MaskedReveal>
          <MaskedReveal delay={0.22}>
            <span className="text-gold">Your Project</span>
          </MaskedReveal>
        </h1>
        <p className="mt-6 max-w-xl mx-auto text-slate-400 text-base sm:text-lg">
          Call, email or send us a message — whatever works for you. Our team usually responds within a few hours.
        </p>
      </div>
    </section>

    <section className="bg-ivory py-20" data-testid="contact-channels">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-5xl">
        {CHANNELS.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.07}>
            <a
              href={c.href}
              target={c.id === "office" ? "_blank" : undefined}
              rel="noopener noreferrer"
              data-testid={`contact-channel-${c.id}`}
              className="block bg-white rounded-2xl border border-slate-200 p-7 h-full hover:border-gold/60 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-gold/10 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-xl bg-navy flex items-center justify-center group-hover:bg-royal transition-colors">
                <c.icon className="text-gold" size={22} />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">{c.t}</p>
              <p className="mt-1 font-display font-bold text-ink break-all">{c.v}</p>
            </a>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-ivory pb-24" data-testid="contact-form-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-10">
        <Reveal className="lg:col-span-3">
          <ContactForm />
        </Reveal>
        <Reveal className="lg:col-span-2" delay={0.1}>
          <div className="bg-navy rounded-3xl p-8 h-full text-white border border-white/10 grain relative overflow-hidden" data-testid="contact-info-card">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gold/15 blur-[80px]" />
            <h3 className="relative font-display text-2xl font-extrabold">RICHIE RICH — HQ</h3>
            <div className="relative mt-7 space-y-5 text-sm">
              <div className="flex items-start gap-3.5">
                <MapPin size={18} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-200">Office Address</p>
                  <p className="text-slate-400 mt-0.5" data-testid="contact-address">{SITE.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3.5">
                <Phone size={18} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-200">Phone</p>
                  <a href={`tel:+${SITE.phoneRaw}`} className="text-slate-400 hover:text-white transition-colors mt-0.5 block" data-testid="contact-phone-link">{SITE.phoneDisplay}</a>
                </div>
              </div>
              <div className="flex items-start gap-3.5">
                <Mail size={18} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-200">Business Email</p>
                  <a href={`mailto:${SITE.email}`} className="text-slate-400 hover:text-white transition-colors mt-0.5 block" data-testid="contact-email-link">{SITE.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-3.5">
                <Clock3 size={18} className="text-gold mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-200">Working Hours</p>
                  <p className="text-slate-400 mt-0.5">Mon – Sat, 10:00 AM – 7:00 PM IST</p>
                </div>
              </div>
            </div>
            <div className="relative mt-8 pt-6 border-t border-white/10">
              <p className="text-xs text-slate-500 leading-relaxed">
                By contacting us you agree to our{" "}
                <Link to="/privacy-policy" className="text-gold-light hover:underline" data-testid="contact-privacy-link">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/terms-of-services" className="text-gold-light hover:underline" data-testid="contact-terms-link">Terms of Services</Link>.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
